import * as Sentry from '@sentry/react';

/**
 * Why: Sentry scope needs safe user identifiers for debugging (id,
 * organization, programs, role) but Rule 6 of the PII sub-spec forbids
 * email / name / phone / ip from ever being attached. Turning a raw
 * auth-user object into the safe subset is its own concern, mirroring
 * the PHP UserContextBuilder service.
 * Scope: Adding a new safe field (e.g. client_id) lands here; callers
 * don't change.
 * SRP: Attach the allow-listed identifiers from an authenticated user
 * to the current Sentry scope.
 *
 * Mirrors `app/Sentry/UserContextBuilder.php` in the API repo.
 *
 * @param {Object|null} user Authenticated user record (shape may vary)
 */
export function setSentryUserContext(user) {
  if (!user || typeof user !== 'object') {
    Sentry.setUser(null);
    return;
  }

  const id = user.id ?? user.user_id ?? null;
  if (id === null) {
    Sentry.setUser(null);
    return;
  }

  const metadata = {};
  if (user.organization_id !== undefined && user.organization_id !== null) {
    metadata.organization_id = user.organization_id;
  }
  const programIds = resolveProgramIds(user);
  if (programIds !== null) {
    metadata.program_ids = programIds;
  }
  const role = resolveRoleName(user);
  if (role !== null) {
    metadata.role = role;
  }

  Sentry.setUser({
    id: String(id),
    ...metadata,
  });
}

/**
 * Why: programs may be attached to the user object as an array or absent
 * entirely, depending on the endpoint that fetched the user. Attach only
 * when readily available — never fire an extra request.
 * SRP: Produce the list of program IDs from whatever program shape the
 * user carries, or null if unavailable.
 */
function resolveProgramIds(user) {
  if (Array.isArray(user.programs)) {
    return user.programs.map((program) => program.id ?? program).filter((id) => id !== undefined);
  }
  if (Array.isArray(user.program_ids)) {
    return user.program_ids;
  }
  return null;
}

/**
 * Why: Role may surface as a string, an array, or a Spatie-style object.
 * This resolver handles the common shapes. Return null if unavailable
 * rather than guessing.
 * SRP: Produce a single role-name string for Sentry scope, or null.
 */
function resolveRoleName(user) {
  if (typeof user.role === 'string' && user.role !== '') {
    return user.role;
  }
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    const first = user.roles[0];
    return typeof first === 'string' ? first : (first && first.name) || null;
  }
  return null;
}
